import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/work.scss';
import { PROJECTS } from '../data/projects';
import goodGrapes2Image from '/assets/portfolio/goodgrapes/GG_2.webp';

gsap.registerPlugin(ScrollTrigger);

const Work = () => {
  const carouselItems = useMemo(
    () =>
      PROJECTS.map((project) => {
        const heroBlock = project.blocks.find((block) => block.type === 'image');
        return {
          slug: project.slug,
          title: project.title,
          subtitle: project.subtitle,
          image: heroBlock ? heroBlock.src : null,
        };
      }).filter((project) => project.image),
    []
  );

  useEffect(() => {
    // Disable scroll-based animations on small screens so content is immediately visible
    if (typeof window !== 'undefined' && window.matchMedia) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        return;
      }
    }

    const rows = document.querySelectorAll('.section.portfolio .row');
    if (!rows.length) return;

    const animations = [];

    rows.forEach((row) => {
      const items = row.querySelectorAll('.portfolio-grid-item');
      if (!items.length) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.fromTo(
        items,
        { opacity: 0, y: 40, filter: 'blur(6px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.15,
        }
      );

      animations.push(tl);
    });

    return () => {
      animations.forEach((tl) => {
        if (tl.scrollTrigger) {
          tl.scrollTrigger.kill();
        }
        tl.kill();
      });
    };
  }, []);

  return (
    <>
      <Seo />
      <div className="work">
      <section className="section portfolio">
        <div className="wrapper">
          <div className="portfolio-heading-row">
            <h2 className="portfolio-heading">Latest Work</h2>
          </div>
        </div>
        <div className="wrapper grid-display">
          {/* First row: col-8 and col-4 */}
          <div className="row">
            {carouselItems.slice(0, 2).map((item, index) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          
          {/* Second row: supergauge and llama */}
          <div className="row">
            {carouselItems.filter(item => item.slug === 'supergauge' || item.slug === 'llama').map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Third row: full width col-12 */}
          <div className="row full-height">
            {carouselItems.slice(5, 6).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Fourth row: good-grapes and sonaris */}
          <div className="row">
            {carouselItems.filter(item => item.slug === 'sonaris').map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
            {(() => {
              const goodGrapesProject = PROJECTS.find(p => p.slug === 'good-grapes');
              return goodGrapesProject ? (
                <div className="portfolio-grid-item" key="good-grapes">
                  <Link
                    to="/projects/good-grapes"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="portfolio-grid-media">
                      <img src={goodGrapes2Image} alt="Good Grapes" />
                    </div>
                    <div className="portfolio-grid-caption">
                      <div className="portfolio-grid-title">Good Grapes</div>
                      <div className="portfolio-grid-subtitle">Wine subscription and discovery service</div>
                    </div>
                  </Link>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Work;
